import React, { useState } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const [showDetails, setShowDetails] = useState(false);

  let errorMessage = "An unexpected application error occurred.";
  let errorStack = "";

  if (isRouteErrorResponse(error)) {
    errorMessage = `${error.status} ${error.statusText}: ${error.data}`;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorStack = error.stack || "";
  } else if (typeof error === "string") {
    errorMessage = error;
  } else if (error && typeof error === "object") {
    errorMessage = (error as any).message || JSON.stringify(error);
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "var(--bg)",
        color: "var(--text)",
        fontFamily: "Inter, system-ui, sans-serif",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "600px",
          backgroundColor: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          padding: "40px 32px",
          boxShadow: "var(--shadow-lg)",
          textAlign: "center",
        }}
      >
        {/* Brand Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "32px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: "var(--accent)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 3px 8px var(--accent-glow)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </div>
          <h2 style={{ fontSize: "20px", fontWeight: 800, margin: 0, letterSpacing: "-0.01em" }}>
            N<span style={{ color: "var(--accent)" }}>Code</span>
          </h2>
        </div>

        {/* Warning Icon */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              borderRadius: "50%",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        <h1 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px", letterSpacing: "-0.01em" }}>
          Oops! Something went wrong
        </h1>
        
        <p style={{ fontSize: "14px", color: "var(--text-2)", lineHeight: "1.6", marginBottom: "32px" }}>
          An error occurred while loading this page. This could be due to a temporary connection issue or an unexpected system error.
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginBottom: "24px" }}>
          <button
            onClick={handleGoHome}
            style={{
              padding: "10px 20px",
              background: "transparent",
              border: "1.5px solid var(--border)",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--text-2)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "var(--surface-2)";
              e.currentTarget.style.borderColor = "var(--border-strong)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            Go to Dashboard
          </button>
          
          <button
            onClick={handleReload}
            style={{
              padding: "10px 20px",
              background: "var(--accent)",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--text-on-accent)",
              cursor: "pointer",
              boxShadow: "0 4px 6px -1px var(--accent-dim)",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "var(--accent-strong)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "var(--accent)";
            }}
          >
            Reload Page
          </button>
        </div>

        {/* Error Details toggle */}
        <div style={{ textAlign: "left", borderTop: "1px solid var(--border-soft)", paddingTop: "20px" }}>
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {showDetails ? "Hide Error Details" : "Show Error Details"}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              style={{ transform: showDetails ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showDetails && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                background: "var(--surface-2)",
                border: "1px solid var(--border-soft)",
                borderRadius: "8px",
                fontSize: "12px",
                lineHeight: "1.6",
                color: "var(--text-2)",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              <strong>Error:</strong> {errorMessage}
              {errorStack && (
                <>
                  <br />
                  <br />
                  <strong>Stack trace:</strong>
                  <br />
                  {errorStack}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteErrorBoundary;
